import { useEffect, useRef, useState } from 'react';
import '../styles.css';
import { sampleSmilSpline } from '../utils/smilSplineSample';
import Button from '../components/Button';
import PageHeader from '../components/PageHeader';
import DemoCard from '../components/DemoCard';
import { AnimationIcon, CodeIcon } from '../icons/navigationIcons';
import {
    EasingGraphFastSlow,
    EasingGraphSlowFast,
    KEY_SPLINE_CURVES,
    KEY_SPLINE_DEMO_MS,
    KEY_SPLINE_SMIL_SNIPPET,
    KEY_SPLINE_TIMES,
    KEY_SPLINE_VALUES,
    PROJECT_EIGHT_LOADING_BLINK_CSS,
    ProjectEightLoadingMark,
    ProjectEightLogoLockup,
    ProjectEightSmilKeySplineDemo,
} from '../localassets/projectEightMotion';

/* Typography: see styles.css — .text-header-main, .text-body, .text-body-sm, .text-inline-code, .text-code-block */

const ProjectEightPage = () => {
    const [showSmilCode, setShowSmilCode] = useState(false);
    const keySplineCircleRef = useRef(null);
    const keySplineStartRef = useRef(null);
    const keySplineRafRef = useRef(null);

    useEffect(() => {
        if (showSmilCode) return undefined;

        const circle = keySplineCircleRef.current;
        if (!circle) return undefined;

        const tick = (now) => {
            if (keySplineStartRef.current === null) keySplineStartRef.current = now;
            const elapsed = (now - keySplineStartRef.current) % KEY_SPLINE_DEMO_MS;
            const u = elapsed / KEY_SPLINE_DEMO_MS;
            const cx = sampleSmilSpline(u, KEY_SPLINE_VALUES, KEY_SPLINE_TIMES, KEY_SPLINE_CURVES);
            circle.setAttribute('cx', String(cx));
            keySplineRafRef.current = requestAnimationFrame(tick);
        };

        keySplineRafRef.current = requestAnimationFrame(tick);
        return () => {
            if (keySplineRafRef.current !== null) cancelAnimationFrame(keySplineRafRef.current);
            keySplineRafRef.current = null;
            keySplineStartRef.current = null;
        };
    }, [showSmilCode]);

    return (
        <div className="scrollable-window scrollable-window--wide">
            <style>{PROJECT_EIGHT_LOADING_BLINK_CSS}</style>
            <div style={styles.wholeThingBox}>
                <div style={styles.boxMain}>
                    <PageHeader title="Lightweight UI motion" />
                    <div className="text-body-sm">
                        A short story about motion design, interfaces, and why I reach for hand-authored SVG when the usual
                        toolchain disappears.
                    </div>
                    <div style={styles.deviderStroke}></div>

                    <div className="text-body">The bottleneck</div>
                    <div className="text-body-sm">
                        It started with a simple observation: only one person in the room knew how to make things move. When I
                        joined BYU Athletics as a student assistant, I was the lowest rung on a team of five full-time professional
                        designers. I watched, I learned, and I noticed something. One of those five designers — the busiest one by far
                        — was the only person who knew After Effects. Motion design was a bottleneck, and nobody was rushing to fix
                        it. So I did.
                    </div>

                    <div className="text-body">After Effects</div>
                    <div className="text-body-sm">
                        I taught myself After Effects. Within a short time I was not just a student assistant grinding through tasks
                        — I was one of two people on the entire team who could animate. That meant something most student designers
                        never get to experience: I saw my work on the video boards at basketball and football games, in front of tens
                        of thousands of fans. That is not a small thing. Motion got me there.
                    </div>

                    <div className="text-body">Motion in the interface</div>
                    <div className="text-body-sm">
                        Around the same time, I was studying UX design and started asking a different question: what does motion
                        look like inside an interface? That search led me to Lottie — a bridge that let you export After Effects
                        animations as lightweight, scalable vectors for web apps. It felt like a superpower.
                    </div>

                    <div className="text-body">New constraints</div>
                    <div className="text-body-sm">
                        Then I changed jobs. At AskElephant, there was no Adobe Creative Cloud license, and the app did not support
                        Lottie. The first time a task needed an animation, I opened my usual tools and found a closed door. The old
                        path was gone.
                    </div>

                    <div className="text-body">Code as the canvas</div>
                    <div className="text-body-sm">
                        I had always been drawn to code as a craft, not just a delivery format. Building a search icon in Figma is
                        satisfying. Authoring the same icon in a text editor — knowing exactly what each coordinate does — feels
                        like something different. So I opened an SVG file and started reading. Once the path commands, transform
                        stack, and coordinate system clicked, motion was the obvious next question: if I can describe shapes with
                        numbers, I can describe movement the same way.
                    </div>

                    <div className="text-body">What I use</div>
                    <div className="text-body-sm">
                        Mostly SMIL and CSS. SVG motion stays lighter than Lottie or Rive when you need it. Where I lean in hardest is
                        SMIL <code className="text-inline-code">calcMode=&quot;spline&quot;</code> with{' '}
                        <code className="text-inline-code">keySplines</code> — the same cubic control points you would dial in with a speed
                        graph in After Effects, just written as numbers instead of dragged handles. Vector points and timing are the
                        same craft; the syntax was what I had to learn.
                    </div>

                    <div className="text-body">SMIL keySplines</div>
                    <div className="text-body-sm">
                        The pacing I care about most: I treat{' '}
                        <code className="text-inline-code">keySplines</code> like stacked speed-curve segments — each interval can use
                        its own curve, the way you would layer eases on a timeline. The example below uses your{' '}
                        <code className="text-inline-code">values</code>, <code className="text-inline-code">keyTimes</code>, and{' '}
                        <code className="text-inline-code">keySplines</code>. Chromium-based browsers do not run SMIL{' '}
                        <code className="text-inline-code">&lt;animate&gt;</code>, so the dot is driven with the same spline pacing in
                        JavaScript. Use the control in the card header to switch between the live preview and the{' '}
                        <code className="text-inline-code">&lt;animate&gt;</code> markup I would ship in a WebKit-capable context.
                    </div>

                    <div style={styles.easeExplainer} role="group" aria-label="How keySplines map to easing graphs">
                        <div className="text-body-sm">
                            Each <code className="text-inline-code">keySplines</code> segment is four numbers — the same{' '}
                            <code className="text-inline-code">x1 y1 x2 y2</code> control points as CSS{' '}
                            <code className="text-inline-code">cubic-bezier</code>. Picture a unit square: horizontal axis is time through
                            the segment (0 → 1), vertical axis is eased progress (0 → 1). The orange curve is the easing path; the small
                            diamonds mark the Bézier handles (<code className="text-inline-code">x1 y1</code> and{' '}
                            <code className="text-inline-code">x2 y2</code>) that SMIL uses to draw it.
                        </div>
                        <div style={styles.easeExplainerRow}>
                            <figure style={styles.easeExplainerFigure}>
                                <EasingGraphSlowFast />
                                <figcaption style={styles.easeExplainerCaption}>
                                    <div className="text-body" style={styles.easeExplainerTitle}>
                                        Slow → fast
                                    </div>
                                    <div className="text-body-sm">
                                        Progress starts gently then accelerates — the curve is shallow at the bottom (slow change in
                                        value) and steep toward the end (fast).
                                    </div>
                                    <code className="text-inline-code" style={styles.easeExplainerCode}>
                                        0.7 0 1 0.3
                                    </code>
                                </figcaption>
                            </figure>
                            <figure style={styles.easeExplainerFigure}>
                                <EasingGraphFastSlow />
                                <figcaption style={styles.easeExplainerCaption}>
                                    <div className="text-body" style={styles.easeExplainerTitle}>
                                        Fast → slow
                                    </div>
                                    <div className="text-body-sm">
                                        Value changes quickly at first, then settles — the mirror shape: steep at the start, flattening
                                        into the end.
                                    </div>
                                    <code className="text-inline-code" style={styles.easeExplainerCode}>
                                        0 0.7 0.3 1
                                    </code>
                                </figcaption>
                            </figure>
                        </div>
                    </div>

                    <DemoCard
                        header="SMIL keySplines (authoring eases in markup)"
                        headerRight={
                            <div className="tooltip-wrapper">
                                <Button
                                    className="app-button--on-surface"
                                    variant="icon"
                                    aria-label={showSmilCode ? 'Show animation preview' : 'Show SMIL source code'}
                                    aria-pressed={showSmilCode}
                                    onClick={() => setShowSmilCode((v) => !v)}
                                    icon={showSmilCode ? <AnimationIcon /> : <CodeIcon />}
                                />
                                <span className="tooltip-text">{showSmilCode ? 'Animation' : 'Code'}</span>
                            </div>
                        }
                    >
                        {showSmilCode ? (
                            <pre className="text-body-sm" style={styles.smilSourceInCard} tabIndex={0}>
                                {KEY_SPLINE_SMIL_SNIPPET}
                            </pre>
                        ) : (
                            <div style={styles.svgWrapCompact} aria-hidden="true">
                                <ProjectEightSmilKeySplineDemo ref={keySplineCircleRef} />
                            </div>
                        )}
                    </DemoCard>

                    <div className="text-body">In the wild</div>
                    <div className="text-body-sm">
                        Shipping examples: hand-authored SVG with the same stacked timing idea as SMIL. The loading mark uses CSS
                        keyframes so it loops everywhere (Chromium does not run SMIL{' '}
                        <code className="text-inline-code">&lt;animate&gt;</code>). The logo lockup recreates the same timing with
                        CSS keyframes and <code className="text-inline-code">@keyframes</code> so masks, opacity, and motion loop
                        in every browser, including Chromium.
                    </div>
                    <DemoCard header="Loading mark (CSS keyframes)">
                        <div style={styles.loadingDemoWrap}>
                            <ProjectEightLoadingMark
                                style={styles.showcaseLoadingSvg}
                                role="img"
                                aria-label="Loading spinner built from intersecting vector paths"
                            />
                        </div>
                        <p className="text-body-sm" style={styles.loadingDemoCaption}>
                            Layered groups blink in sequence so the intersection reads as motion without raster assets.
                        </p>
                    </DemoCard>

                    <DemoCard header="Logo lockup (CSS keyframes)">
                        <div style={styles.logoLockupDemoWrap}>
                            <ProjectEightLogoLockup
                                role="img"
                                aria-label="Animated wordmark and logo with reveal masks and eased motion"
                            />
                        </div>
                        <p className="text-body-sm" style={styles.loadingDemoCaption}>
                            Wordmark steps in, the mark slides into place, and two masks sweep with different rotations so the
                            shapes resolve together.
                        </p>
                    </DemoCard>

                    <div className="text-body">Today</div>
                    <div className="text-body-sm">
                        Now I code SVG animations for fun as much as for function — sometimes with AI assistance, occasionally
                        entirely on my own. Not because it is always the practical choice, but because I can. And because somewhere
                        between a crowded basketball arena and a blank code editor, I decided that is the kind of designer I wanted
                        to be.
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    wholeThingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '40px',
        alignSelf: 'stretch',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
    boxMain: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '20px',
        width: 'var(--content-width)',
        maxWidth: '100%',
        minWidth: 0,
        padding: '0 clamp(1rem, 4vw, 2rem)',
        boxSizing: 'border-box',
        margin: '0',
        position: 'relative',
        zIndex: 1,
    },
    deviderStroke: {
        height: '1px',
        backgroundColor: 'var(--color-border)',
        alignSelf: 'stretch',
    },
    svgWrapCompact: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 0,
        height: '280px',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
    easeExplainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '16px',
        alignSelf: 'stretch',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
    easeExplainerRow: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 'clamp(24px, 5vw, 48px)',
        width: '100%',
        minWidth: 0,
    },
    easeExplainerFigure: {
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        padding: '20px 0px',
        flex: '1 1 240px',
        maxWidth: 'min(100%, 360px)',
        minWidth: 0,
    },
    easeExplainerCaption: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        textAlign: 'center',
        width: '100%',
    },
    easeExplainerTitle: {
        margin: 0,
    },
    easeExplainerCode: {
        marginTop: '4px',
    },
    loadingDemoWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100px',
        padding: '40px 20px',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
    showcaseLoadingSvg: {
        display: 'block',
        width: '120px',
        height: 'auto',
    },
    loadingDemoCaption: {
        margin: 0,
    },
    logoLockupDemoWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
    smilSourceInCard: {
        margin: 0,
        boxSizing: 'border-box',
        display: 'block',
        height: '280px',
        alignSelf: 'stretch',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        whiteSpace: 'pre',
        wordBreak: 'normal',
    },
};

export default ProjectEightPage;
